import { Repository } from "react3l-common";
import { kebabCase } from "lodash";
import { httpConfig } from "config/http";
import { BASE_API_URL } from "config/consts";
import { map, Observable } from "rxjs";
import { AxiosResponse } from "axios";
import nameof from "ts-nameof.macro";
import { API_INTEGRATION_EAP_CONFIGURATION_PREFIX } from "config/api-consts";
import { Menu, MenuFilter } from "models/Menu";
import { Site, SiteFilter } from "models/Site";
import { Page, PageFilter } from "models/Page";
import { EnumFilter, EnumModel } from "models/Enum";
import { FileTemplate } from "models/FileTemplate";

export type KeyType = string | number;

export class MenuRepository extends Repository {
  constructor() {
    super(httpConfig);
    this.baseURL = new URL(API_INTEGRATION_EAP_CONFIGURATION_PREFIX, BASE_API_URL).href;
  }

  public listEnableIntegrationEap = (
    menuFilter?: MenuFilter
  ): Observable<Menu[]> => {
    return this.http
      .post<Menu[]>(kebabCase(nameof(this.listEnableIntegrationEap)), menuFilter)
      .pipe(Repository.responseMapToList<Menu>(Menu));
  };

  public updateEnableIntegrationEap = (menu: Menu[]): Observable<Menu[]> => {
    return this.http
      .post<Menu[]>(kebabCase(nameof(this.updateEnableIntegrationEap)), menu)
      .pipe(Repository.responseMapToList<Menu>(Menu));
  };

  public update = (menu: Menu): Observable<Menu> => {
    return this.http
      .post<Menu>(kebabCase(nameof(this.update)), menu)
      .pipe(Repository.responseMapToModel<Menu>(Menu));
  };

  public count = (menuFilter?: MenuFilter): Observable<number> => {
    return this.http
      .post<number>(kebabCase(nameof(this.count)), menuFilter)
      .pipe(Repository.responseDataMapper<number>());
  };

  public list = (
    menuFilter?: MenuFilter
  ): Observable<Menu[]> => {
    return this.http
      .post<Menu[]>(kebabCase(nameof(this.list)), menuFilter)
      .pipe(Repository.responseMapToList<Menu>(Menu));
  };

  public get = (id: number): Observable<Menu> => {
    return this.http
      .post<Menu>(kebabCase(nameof(this.get)), { id })
      .pipe(Repository.responseMapToModel<Menu>(Menu));
  };

  public create = (menu: Menu): Observable<Menu> => {
    return this.http
      .post<Menu>(kebabCase(nameof(this.create)), menu)
      .pipe(Repository.responseMapToModel<Menu>(Menu));
  };


  public delete = (menu: Menu): Observable<Menu> => {
    return this.http
      .post<Menu>(kebabCase(nameof(this.delete)), menu)
      .pipe(Repository.responseMapToModel<Menu>(Menu));
  };

  public bulkDelete = (idList: KeyType[]): Observable<void> => {
    return this.http
      .post(kebabCase(nameof(this.bulkDelete)), idList)
      .pipe(Repository.responseDataMapper());
  };

  public save = (menu: Menu): Observable<Menu> => {
    return menu.id ? this.update(menu) : this.create(menu);
  };

  public upload = (files?: File[] | Blob[]): Observable<any> => {
    const formData: FormData = new FormData();
    formData.append("file", files[0] as Blob);
    return this.http
      .post<File>(kebabCase(nameof(this.upload)), formData)
      .pipe(map((r) => [r.data]));
  };

  public import = (
    file: File,
    name: string = nameof(file)
  ): Observable<void> => {
    const formData: FormData = new FormData();
    formData.append(name, file as Blob);
    return this.http
      .post<void>(kebabCase(nameof(this.import)), formData)
      .pipe(Repository.responseDataMapper<any>());
  };

  public export = (filter: any): Observable<AxiosResponse<any>> => {
    return this.http.post("export", filter, {
      responseType: "arraybuffer",
    });
  };

  public exportTemplate = (): Observable<AxiosResponse<any>> => {
    return this.http.post(
      "export-template",
      {},
      {
        responseType: "arraybuffer",
      }
    );
  };

  public filterListSite = (siteFilter: SiteFilter): Observable<Site[]> => {
    return this.http
      .post<Site[]>(kebabCase(nameof(this.filterListSite)), siteFilter)
      .pipe(Repository.responseMapToList<Site>(Site));
  };

  public filterListMenu = (menuFilter: MenuFilter): Observable<Menu[]> => {
    return this.http
      .post<Menu[]>(kebabCase(nameof(this.filterListMenu)), menuFilter)
      .pipe(Repository.responseMapToList<Menu>(Menu));
  };

  public filterListPage = (pageFilter: PageFilter): Observable<Page[]> => {
    return this.http
      .post<Page[]>(kebabCase(nameof(this.filterListPage)), pageFilter)
      .pipe(Repository.responseMapToList<Page>(Page));
  };

  public filterListDefault = (
    defaultFilter: EnumFilter
  ): Observable<EnumModel[]> => {
    return this.http
      .post<EnumModel[]>(
        kebabCase(nameof(this.filterListDefault)),
        defaultFilter
      )
      .pipe(Repository.responseMapToList<EnumModel>(EnumModel));
  };

  public validateInputs = (
    fileTemplate: FileTemplate
  ): Observable<FileTemplate> => {
    return this.http
      .post<FileTemplate>(kebabCase(nameof(this.validateInputs)), fileTemplate)
      .pipe(Repository.responseMapToModel<FileTemplate>(FileTemplate));
  };
}

export const menuRepository = new MenuRepository();
